/**
 * Open Food Facts (OFF) product lookup and mapping onto the nutrient catalog.
 *
 * OFF v2 API: GET https://world.openfoodfacts.org/api/v2/product/{code}.json?fields=...
 * Data is ODbL: the app must show "Data from Open Food Facts". OFF asks every client to send a
 * User-Agent naming the app. Mapping notes:
 *  - `<key>_100g` values are in grams for every mass nutrient (vitamins too), kcal for energy-kcal.
 *  - `alcohol` is % vol, not grams; converted with ethanol's density (0.789 g/ml) and flagged.
 *  - US labels print TOTAL carbohydrate (fiber included); EU labels print available carbohydrate
 *    with fiber listed separately. The catalog's carbs_g is total, so fiber is added back for EU data.
 */
import { NUTRIENT_BY_KEY, maxPer100g, roundNutrient, scaleNutrients, type NutrientMap, type NutrientSourceMap } from '../nutrition/catalog';
import { LESS_THAN_ONE, analyzeLabel, type DeclaredGrams, type DeclaredLabel, type TrueLabelReport } from '../tef';

export const OFF_API_BASE = 'https://world.openfoodfacts.org/api/v2/product/';
export const OFF_ATTRIBUTION = 'Data from Open Food Facts (openfoodfacts.org), available under the Open Database License.';

export const OFF_FIELDS: readonly string[] = [
  'code', 'product_name', 'product_name_en', 'generic_name', 'brands', 'quantity', 'serving_size', 'serving_quantity',
  'nutrition_data_per', 'nutriments', 'countries_tags', 'categories_tags', 'image_front_small_url', 'image_front_url',
  'nutriscore_grade', 'nova_group', 'ingredients_text', 'ingredients_text_en', 'allergens_tags', 'last_modified_t',
];

/** OFF nutriment key -> catalog key and the factor from OFF's per-100 g grams to the catalog unit. */
const MAP: ReadonlyArray<readonly [off: string, key: string, factor: number]> = [
  ['proteins', 'protein_g', 1],
  ['sugars', 'sugars_g', 1],
  ['added-sugars', 'added_sugars_g', 1],
  ['starch', 'starch_g', 1],
  ['polyols', 'sugar_alcohols_g', 1],
  ['fiber', 'fiber_g', 1],
  ['soluble-fiber', 'fiber_soluble_g', 1],
  ['insoluble-fiber', 'fiber_insoluble_g', 1],
  ['lactose', 'lactose_g', 1],
  ['sucrose', 'sucrose_g', 1],
  ['glucose', 'glucose_g', 1],
  ['fructose', 'fructose_g', 1],
  ['galactose', 'galactose_g', 1],
  ['maltose', 'maltose_g', 1],
  ['fat', 'fat_g', 1],
  ['saturated-fat', 'saturated_g', 1],
  ['monounsaturated-fat', 'mufa_g', 1],
  ['polyunsaturated-fat', 'pufa_g', 1],
  ['trans-fat', 'trans_g', 1],
  ['omega-3-fat', 'omega3_g', 1],
  ['omega-6-fat', 'omega6_g', 1],
  ['alpha-linolenic-acid', 'ala_g', 1],
  ['docosahexaenoic-acid', 'dha_g', 1],
  ['eicosapentaenoic-acid', 'epa_g', 1],
  ['linoleic-acid', 'la_g', 1],
  ['arachidonic-acid', 'aa_g', 1],
  ['cholesterol', 'cholesterol_mg', 1e3],
  ['potassium', 'potassium_mg', 1e3],
  ['calcium', 'calcium_mg', 1e3],
  ['iron', 'iron_mg', 1e3],
  ['magnesium', 'magnesium_mg', 1e3],
  ['phosphorus', 'phosphorus_mg', 1e3],
  ['zinc', 'zinc_mg', 1e3],
  ['copper', 'copper_mg', 1e3],
  ['manganese', 'manganese_mg', 1e3],
  ['selenium', 'selenium_ug', 1e6],
  ['iodine', 'iodine_ug', 1e6],
  ['chromium', 'chromium_ug', 1e6],
  ['molybdenum', 'molybdenum_ug', 1e6],
  ['fluoride', 'fluoride_ug', 1e6],
  ['vitamin-a', 'vit_a_ug', 1e6],
  ['beta-carotene', 'beta_carotene_ug', 1e6],
  ['vitamin-c', 'vit_c_mg', 1e3],
  // 1 ug vitamin D = 40 IU.
  ['vitamin-d', 'vit_d_iu', 1e6 * 40],
  ['vitamin-e', 'vit_e_mg', 1e3],
  ['vitamin-k', 'vit_k_ug', 1e6],
  ['vitamin-b1', 'vit_b1_mg', 1e3],
  ['vitamin-b2', 'vit_b2_mg', 1e3],
  ['vitamin-pp', 'vit_b3_mg', 1e3],
  ['pantothenic-acid', 'vit_b5_mg', 1e3],
  ['vitamin-b6', 'vit_b6_mg', 1e3],
  ['vitamin-b9', 'folate_ug', 1e6],
  ['folates', 'folate_ug', 1e6],
  ['vitamin-b12', 'vit_b12_ug', 1e6],
  ['biotin', 'biotin_ug', 1e6],
  ['choline', 'choline_mg', 1e3],
  ['caffeine', 'caffeine_mg', 1e3],
];

const ETHANOL_G_PER_ML = 0.789;
const KJ_PER_KCAL = 4.184;
const NORTH_AMERICA: readonly string[] = ['en:united-states', 'en:canada', 'en:puerto-rico'];

export interface OffProductSummary {
  gtin: string;
  name: string;
  brand: string | null;
  quantity: string | null;
  imageUrl: string | null;
  nutriscore: string | null;
  novaGroup: number | null;
  ingredients: string | null;
  allergens: string[];
  categories: string[];
  countries: string[];
  lastModified: string | null;
}

export interface ServingInfo {
  grams: number;
  /** As printed, e.g. "2 tbsp (30 g)". */
  label: string | null;
}

export interface BarcodeFound {
  found: true;
  gtin: string;
  source: 'open_food_facts';
  attribution: string;
  product: OffProductSummary;
  /** Per 100 g, catalog keys. A missing key means unknown, never zero. */
  per100g: NutrientMap;
  sources: NutrientSourceMap;
  /** How the label's carbohydrate line was read. */
  carbBasis: 'total' | 'available';
  serving: ServingInfo | null;
  perServing: NutrientMap | null;
  /** Nutrition Facts check; null when the data is not a US per-serving label (reason in labelCheckSkipped). */
  labelCheck: { declared: DeclaredLabel; report: TrueLabelReport } | null;
  labelCheckSkipped: string | null;
  warnings: string[];
}

export interface BarcodeNotFound {
  found: false;
  gtin: string;
  tried: string[];
}

export type BarcodeLookup = BarcodeFound | BarcodeNotFound;

const num = (v: unknown): number | null => {
  const n: number = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : Number.NaN;
  return Number.isFinite(n) ? n : null;
};
const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v.trim() : null);
const strList = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);
/** "en:gluten" -> "gluten". */
const tagName = (t: string): string => t.replace(/^[a-z]{2}:/, '').replace(/-/g, ' ');

/**
 * Carb basis: North American labels print total carbohydrate; others print available carbohydrate.
 * Only products sold solely in North America are read as total; the result reports which was used.
 */
export function carbBasisFor(countries: readonly string[]): 'total' | 'available' {
  return countries.length > 0 && countries.every((c) => NORTH_AMERICA.includes(c)) ? 'total' : 'available';
}

/** Maps OFF `nutriments` (per 100 g) onto catalog keys. */
export function mapNutriments(nutriments: Record<string, unknown>, basis: 'total' | 'available', warnings: string[]): NutrientMap {
  const out: NutrientMap = {};
  const put = (key: string, value: number | null): void => {
    if (value === null || value < 0) return;
    const def = NUTRIENT_BY_KEY.get(key);
    if (!def) return;
    if (value > maxPer100g(def)) { warnings.push(`Ignored ${def.name}: ${roundNutrient(value)} ${def.unit} per 100 g is not plausible.`); return; }
    out[key] = roundNutrient(value);
  };
  const per100 = (k: string): number | null => num(nutriments[`${k}_100g`]);

  let kcal: number | null = per100('energy-kcal');
  if (kcal === null) {
    const kj: number | null = per100('energy-kj') ?? per100('energy');
    if (kj !== null) kcal = kj / KJ_PER_KCAL;
  }
  put('energy_kcal', kcal);

  for (const [off, key, factor] of MAP) {
    if (out[key] !== undefined) continue; // folates vs vitamin-b9: first one wins
    const v: number | null = per100(off);
    if (v !== null) put(key, v * factor);
  }

  let sodiumG: number | null = per100('sodium');
  if (sodiumG === null) {
    const salt: number | null = per100('salt');
    if (salt !== null) sodiumG = salt / 2.5;
  }
  if (sodiumG !== null) put('sodium_mg', sodiumG * 1e3);

  const carbs: number | null = per100('carbohydrates');
  if (carbs !== null) {
    const fiber: number = out.fiber_g ?? 0;
    put('carbs_g', basis === 'available' ? carbs + fiber : carbs);
  }

  const alcoholVol: number | null = per100('alcohol');
  if (alcoholVol !== null && alcoholVol > 0) {
    put('alcohol_g', alcoholVol * ETHANOL_G_PER_ML);
    warnings.push('Alcohol is listed as % by volume; grams are estimated from ethanol density (per 100 ml treated as per 100 g).');
  }
  return out;
}

function declaredGrams(v: number | null): DeclaredGrams | null {
  if (v === null || v < 0) return null;
  return v > 0 && v < 1 ? LESS_THAN_ONE : v;
}

/**
 * A Nutrition Facts check only means something on the numbers as printed, so it runs only for
 * North American products whose nutrition was entered per serving.
 */
export function labelFromOff(nutriments: Record<string, unknown>, per: string | null, basis: 'total' | 'available', servingG: number | null): { declared: DeclaredLabel } | { skipped: string } {
  if (basis !== 'total') return { skipped: 'The label check follows US FDA rounding rules, so it runs on US and Canadian labels only.' };
  if (per !== 'serving') return { skipped: 'This product was entered per 100 g, not as printed per serving, so the rounding check would not be meaningful.' };
  const s = (k: string): number | null => num(nutriments[`${k}_serving`]);
  const kcal: number | null = s('energy-kcal');
  const fat: number | null = s('fat');
  const carbs: DeclaredGrams | null = declaredGrams(s('carbohydrates'));
  const protein: DeclaredGrams | null = declaredGrams(s('proteins'));
  if (kcal === null || fat === null || carbs === null || protein === null || kcal < 0 || fat < 0) {
    return { skipped: 'The per-serving calories, fat, carbohydrate or protein are missing.' };
  }
  return {
    declared: {
      caloriesKcal: kcal, fatG: fat, totalCarbG: carbs, proteinG: protein,
      fiberG: declaredGrams(s('fiber')), sugarAlcoholG: declaredGrams(s('polyols')), servingSizeG: servingG, servingsPerContainer: null,
    },
  };
}

/** Turns an OFF v2 response body into a lookup result (pure). */
export function parseOffResponse(body: unknown, gtin: string, tried: string[]): BarcodeLookup {
  const b = (body ?? {}) as { status?: unknown; product?: unknown; code?: unknown };
  const product = b.product as Record<string, unknown> | undefined;
  if (b.status !== 1 || !product || typeof product !== 'object') return { found: false, gtin, tried };

  const warnings: string[] = [];
  const countries: string[] = strList(product.countries_tags);
  const basis: 'total' | 'available' = carbBasisFor(countries);
  const nutriments = (product.nutriments && typeof product.nutriments === 'object' ? product.nutriments : {}) as Record<string, unknown>;
  const per100g: NutrientMap = mapNutriments(nutriments, basis, warnings);
  if (Object.keys(per100g).length === 0) warnings.push('Open Food Facts has no nutrition data for this product yet.');
  const sources: NutrientSourceMap = Object.fromEntries(Object.keys(per100g).map((k) => [k, 'label'])) as NutrientSourceMap;

  const servingG: number | null = num(product.serving_quantity);
  const serving: ServingInfo | null = servingG !== null && servingG > 0 && servingG <= 5000 ? { grams: servingG, label: str(product.serving_size) } : null;
  const perServing: NutrientMap | null = serving && Object.keys(per100g).length > 0 ? scaleNutrients(per100g, serving.grams) : null;

  let labelCheck: BarcodeFound['labelCheck'] = null;
  let labelCheckSkipped: string | null = null;
  const lab = labelFromOff(nutriments, str(product.nutrition_data_per), basis, serving?.grams ?? null);
  if ('declared' in lab) labelCheck = { declared: lab.declared, report: analyzeLabel(lab.declared) };
  else labelCheckSkipped = lab.skipped;

  const lastModified: number | null = num(product.last_modified_t);
  const name: string = str(product.product_name_en) ?? str(product.product_name) ?? str(product.generic_name) ?? `Product ${gtin}`;
  return {
    found: true,
    gtin,
    source: 'open_food_facts',
    attribution: OFF_ATTRIBUTION,
    product: {
      gtin,
      name: name.slice(0, 300),
      brand: str(product.brands)?.split(',')[0]?.trim().slice(0, 120) ?? null,
      quantity: str(product.quantity),
      imageUrl: str(product.image_front_small_url) ?? str(product.image_front_url),
      nutriscore: str(product.nutriscore_grade),
      novaGroup: num(product.nova_group),
      ingredients: (str(product.ingredients_text_en) ?? str(product.ingredients_text))?.slice(0, 2000) ?? null,
      allergens: strList(product.allergens_tags).map(tagName),
      categories: strList(product.categories_tags).slice(0, 8).map(tagName),
      countries: countries.map(tagName),
      lastModified: lastModified !== null ? new Date(lastModified * 1000).toISOString() : null,
    },
    per100g,
    sources,
    carbBasis: basis,
    serving,
    perServing,
    labelCheck,
    labelCheckSkipped,
    warnings,
  };
}
